import { observable, runInAction } from 'mobx';
import { toast } from 'sonner';

/** How long a decline can be undone */
export const UNDO_WINDOW_MS = 5000;

/** What a screen wants to hear about a decline it started */
export type DeclineCallbacks = {
  /** The request is back, by Undo or because the decline failed */
  onRestore?: () => void;
  /** The decline went through, and the lists no longer hold the request */
  onSent?: () => void;
};

type Decline = DeclineCallbacks & {
  /** The account that holds the request */
  id: string;
  name: string;
  /** Sends the decline, and settles once the lists no longer hold the request or the send has failed. It says whether
   * the decline went through. */
  send: () => Promise<boolean>;
};

type Waiting = {
  timer: ReturnType<typeof setTimeout>;
  toastId: string | number;
  decline: Decline;
};

/** The declines that wait out their Undo. A declined request is hidden at once and a toast offers Undo, and the decline
 * is sent when the toast has run out. Only Undo cancels it. The store belongs to the app, not to the screen that
 * started the decline, so leaving the screen changes nothing. The pending declines are sent when the window closes. */
export class PendingDeclinesStore {
  private readonly waiting = new Map<string, Waiting>();
  // The accounts whose request is hidden: from the decline until its send has settled
  private readonly hidden = observable.set<string>();

  /** Whether the request of this account is declined and so hidden, with its decline waiting or on its way */
  has = (id: string): boolean => this.hidden.has(id);

  start = (decline: Decline) => {
    const { id, name } = decline;
    runInAction(() => this.hidden.add(id));
    const toastId = toast(`Declined ${name}'s request`, {
      duration: UNDO_WINDOW_MS,
      action: { label: 'Undo', onClick: () => this.undo(id) },
    });
    const timer = setTimeout(() => void this.send(id), UNDO_WINDOW_MS);
    this.waiting.set(id, { timer, toastId, decline });
  };

  /** Sends every decline that still waits, as the window closes */
  sendAll = () => {
    for (const id of [...this.waiting.keys()]) void this.send(id);
  };

  private release = (id: string) => runInAction(() => this.hidden.delete(id));

  // Takes the decline off the waiting list, which is what ends its Undo. A click on the toast can still reach it while
  // it fades out, and then there is nothing to undo.
  private take = (id: string): Decline | undefined => {
    const waiting = this.waiting.get(id);
    if (!waiting) return undefined;
    clearTimeout(waiting.timer);
    toast.dismiss(waiting.toastId);
    this.waiting.delete(id);
    return waiting.decline;
  };

  private undo = (id: string) => {
    const decline = this.take(id);
    if (!decline) return;
    this.release(id);
    decline.onRestore?.();
  };

  private send = async (id: string) => {
    const decline = this.take(id);
    if (!decline) return;
    const didDecline = await decline.send();
    this.release(id);
    if (didDecline) decline.onSent?.();
    else decline.onRestore?.();
  };
}

export const pendingDeclines = new PendingDeclinesStore();

window.addEventListener('beforeunload', pendingDeclines.sendAll);
